/**
 * TanStack Query hooks for the platform API.
 *
 * Mutations that change a registration or an agency invalidate the overview too
 * — the sidebar badge and the dashboard counts are derived from the same rows,
 * and a stale badge is how a new registration gets missed.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type {
  Agency,
  AgencyCounts,
  AgencyUsage,
  BillingCycle,
  DevInvite,
  Entitlements,
  ImpersonationSession,
  NextNotice,
  Overview,
  PlanCatalogue,
  PlanRequest,
  PlanRequestCounts,
  PlanRequestStatus,
  PlatformAdmin,
  Registration,
  RegistrationCounts,
  PaymentMethod,
  RegistrationStatus,
  SubscriptionStatus,
} from '../lib/types';

export const keys = {
  overview: ['overview'] as const,
  registrations: (filters: RegistrationFilters) => ['registrations', filters] as const,
  registration: (id: string) => ['registration', id] as const,
  agencies: (filters: AgencyFilters) => ['agencies', filters] as const,
  agency: (id: string) => ['agency', id] as const,
  admins: ['admins'] as const,
  plans: ['plans'] as const,
  planRequests: (status: string) => ['plan-requests', status] as const,
};

export interface RegistrationFilters {
  status: RegistrationStatus | 'all';
  search: string;
}
export interface AgencyFilters {
  status: 'all' | 'active' | 'inactive' | 'pending';
  search: string;
}

/** Everything the console shows is shared state edited by a small team. */
const LIVE = { staleTime: 15_000 };

// ---------------------------------------------------------------- overview --

export const useOverview = () =>
  useQuery({
    queryKey: keys.overview,
    queryFn: async (): Promise<Overview> => (await api.get('/platform/overview')).data.data,
    ...LIVE,
  });

// ----------------------------------------------------------- registrations --

interface RegistrationList {
  data: Registration[];
  counts: RegistrationCounts;
}

/**
 * The plan requests waiting to be worked.
 *
 * Approving applies exactly what the agency chose and records the payment, so
 * nobody retypes a cycle and a list of add-ons out of an email — which is where
 * a customer ends up on the wrong plan. Every mutation invalidates the overview
 * too: the sidebar badge is derived from the same rows.
 */
export const usePlanRequests = (status: PlanRequestStatus | 'all' = 'pending') =>
  useQuery({
    queryKey: keys.planRequests(status),
    queryFn: async (): Promise<{ data: PlanRequest[]; counts: PlanRequestCounts }> => {
      const { data } = await api.get('/platform/plan-requests', { params: { status } });
      return { data: data.data, counts: data.counts };
    },
    ...LIVE,
  });

export interface ApprovePlanVars {
  id: string;
  method?: string;
  reference?: string;
  note?: string;
  receivedAt?: string;
}

export const useApprovePlanRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: ApprovePlanVars) => {
      const { data } = await api.post(`/platform/plan-requests/${id}/approve`, body);
      return data as { message: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan-requests'] });
      qc.invalidateQueries({ queryKey: keys.overview });
      qc.invalidateQueries({ queryKey: ['agencies'] });
      qc.invalidateQueries({ queryKey: ['agency'] });
    },
  });
};

export const useDeclinePlanRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await api.post(`/platform/plan-requests/${id}/decline`, { reason });
      return data as { message: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan-requests'] });
      qc.invalidateQueries({ queryKey: keys.overview });
    },
  });
};

export const useRegistrations = (filters: RegistrationFilters) =>
  useQuery({
    queryKey: keys.registrations(filters),
    queryFn: async (): Promise<RegistrationList> => {
      const { data } = await api.get('/platform/registrations', {
        params: { status: filters.status, search: filters.search || undefined },
      });
      return { data: data.data, counts: data.counts };
    },
    ...LIVE,
  });

export const useRegistration = (id: string | null) =>
  useQuery({
    queryKey: keys.registration(id ?? ''),
    queryFn: async (): Promise<Registration> =>
      (await api.get(`/platform/registrations/${id}`)).data.data,
    enabled: Boolean(id),
  });

/**
 * Invalidate everything a registration, agency or subscription change can affect.
 *
 * `['agency']` and `['agencies']` are separate keys — the detail page and the
 * directory. Refreshing only the list once left the subscription panel showing
 * the old plan right after saving a new one, with a "Plan updated" notice above
 * stale numbers.
 */
function useInvalidateWorkQueues() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['registrations'] });
    void qc.invalidateQueries({ queryKey: ['registration'] });
    void qc.invalidateQueries({ queryKey: ['agencies'] });
    void qc.invalidateQueries({ queryKey: ['agency'] });
    void qc.invalidateQueries({ queryKey: keys.overview });
  };
}

export const useUpdateRegistration = () => {
  const invalidate = useInvalidateWorkQueues();
  return useMutation({
    mutationFn: async (vars: { id: string; status?: 'new' | 'reviewing'; notes?: string }) => {
      const { id, ...body } = vars;
      return (await api.patch(`/platform/registrations/${id}`, body)).data.data as Registration;
    },
    onSuccess: invalidate,
  });
};

export interface ApproveVars {
  id: string;
  /** Corrections made at approval time — a typo'd email would strand the invite. */
  agencyName?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  notes?: string;
}

export const useApproveRegistration = () => {
  const invalidate = useInvalidateWorkQueues();
  return useMutation({
    mutationFn: async ({ id, ...body }: ApproveVars) => {
      const { data } = await api.post(`/platform/registrations/${id}/approve`, body);
      return data as DevInvite & { message: string; data: { agency: Agency } };
    },
    onSuccess: invalidate,
  });
};

export const useRejectRegistration = () => {
  const invalidate = useInvalidateWorkQueues();
  return useMutation({
    mutationFn: async (vars: { id: string; reason?: string; notify?: boolean }) => {
      const { id, ...body } = vars;
      return (await api.post(`/platform/registrations/${id}/reject`, body)).data.data as Registration;
    },
    onSuccess: invalidate,
  });
};

// ---------------------------------------------------------------- agencies --

interface AgencyList {
  data: Agency[];
  counts: AgencyCounts;
}

export const useAgencies = (filters: AgencyFilters) =>
  useQuery({
    queryKey: keys.agencies(filters),
    queryFn: async (): Promise<AgencyList> => {
      const { data } = await api.get('/platform/agencies', {
        params: { status: filters.status, search: filters.search || undefined },
      });
      return { data: data.data, counts: data.counts };
    },
    ...LIVE,
  });

export const useAgency = (id: string | undefined) =>
  useQuery({
    queryKey: keys.agency(id ?? ''),
    queryFn: async (): Promise<{
      agency: Agency;
      usage: AgencyUsage;
      nextNotice: NextNotice | null;
    }> => (await api.get(`/platform/agencies/${id}`)).data.data,
    enabled: Boolean(id),
  });

export interface OnboardVars {
  agencyName: string;
  contactName: string;
  phone: string;
  email: string;
  address?: string;
  website?: string;
}

export const useOnboardAgency = () => {
  const invalidate = useInvalidateWorkQueues();
  return useMutation({
    mutationFn: async (body: OnboardVars) => {
      const { data } = await api.post('/platform/agencies', body);
      return data as DevInvite & { message: string; data: Agency };
    },
    onSuccess: invalidate,
  });
};

export const useSetAgencyStatus = () => {
  const invalidate = useInvalidateWorkQueues();
  return useMutation({
    mutationFn: async (vars: { id: string; status: 'active' | 'inactive' }) =>
      (await api.patch(`/platform/agencies/${vars.id}/status`, { status: vars.status })).data
        .data as Agency,
    onSuccess: invalidate,
  });
};

/**
 * Delete an agency and everything it owns. Owner-only and irreversible — the
 * API re-checks both the role and the typed name, so neither guard depends on
 * this console behaving.
 */
export const useDeleteAgency = () => {
  const invalidate = useInvalidateWorkQueues();
  return useMutation({
    mutationFn: async (vars: { id: string; confirm: string; reason: string }) => {
      const { data } = await api.delete(`/platform/agencies/${vars.id}`, {
        data: { confirm: vars.confirm, reason: vars.reason },
      });
      return data as {
        message: string;
        removed: Record<string, number>;
        filesRemoved: number;
      };
    },
    onSuccess: invalidate,
  });
};

// ------------------------------------------------------------------ admins --

export const useAdmins = () =>
  useQuery({
    queryKey: keys.admins,
    queryFn: async (): Promise<PlatformAdmin[]> => (await api.get('/platform/admins')).data.data,
  });

export const useCreateAdmin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      email: string;
      password: string;
      role: 'owner' | 'admin';
    }) => (await api.post('/platform/admins', body)).data.data as PlatformAdmin,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admins }),
  });
};

export const useUpdateAdmin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      status?: 'active' | 'inactive';
      role?: 'owner' | 'admin';
    }) => {
      const { id, ...body } = vars;
      return (await api.patch(`/platform/admins/${id}`, body)).data.data as PlatformAdmin;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admins }),
  });
};

// ----------------------------------------------------------- subscriptions --

/** The price list, served by the API so the console keeps no second copy. */
export const usePlans = () =>
  useQuery({
    queryKey: keys.plans,
    queryFn: async (): Promise<PlanCatalogue> => (await api.get('/platform/plans')).data.data,
    staleTime: 5 * 60_000, // prices do not move during a session
  });

export interface SubscriptionVars {
  id: string;
  billingCycle?: BillingCycle;
  addOns?: string[];
  status?: SubscriptionStatus;
  trialEndsAt?: string;
}

export const useUpdateSubscription = () => {
  const invalidate = useInvalidateWorkQueues();
  return useMutation({
    mutationFn: async ({ id, ...body }: SubscriptionVars) => {
      const { data } = await api.put(`/platform/agencies/${id}/subscription`, body);
      return data as { data: Agency; entitlements: Entitlements };
    },
    onSuccess: invalidate,
  });
};

export interface RecordPaymentVars {
  id: string;
  /** Rupees actually received — may differ from what was billed. */
  amount?: number;
  /** The day the money arrived, not the day it was keyed in. */
  paidOn?: string;
  method?: PaymentMethod;
  /** UPI ref, cheque number, UTR. */
  reference?: string;
  note?: string;
}

export const useRecordPayment = () => {
  const invalidate = useInvalidateWorkQueues();
  return useMutation({
    mutationFn: async ({ id, ...body }: RecordPaymentVars) => {
      const { data } = await api.post(`/platform/agencies/${id}/subscription/payment`, body);
      return data as { message: string; data: Agency; entitlements: Entitlements };
    },
    onSuccess: invalidate,
  });
};

/**
 * Open a read-only support session. Not a query — each call mints a new
 * short-lived token and writes an audit row, so it must never be cached or
 * re-run on a refetch.
 */
export const useImpersonate = () =>
  useMutation({
    mutationFn: async (vars: { id: string; reason?: string }) => {
      const { data } = await api.post(`/platform/agencies/${vars.id}/impersonate`, {
        reason: vars.reason,
      });
      return data.data as ImpersonationSession;
    },
  });

export const useChangePassword = () =>
  useMutation({
    mutationFn: async (body: { currentPassword: string; newPassword: string }) =>
      (await api.put('/platform/auth/change-password', body)).data,
  });
